import React from 'react';
import { useTranslation } from 'localization';
import { ApproachReference } from 'types/approach';
import type { ResolvedApproach } from '../../../hooks/useResolvedApproaches';
import { formatSystemName } from './formatSystemName';
import './ContainedApproachesSummaryMolecule.scss';

export interface ContainedApproachesSummaryMoleculeProps {
  subApproaches: ApproachReference[];
  resolvedApproaches?: ResolvedApproach[];
  loading?: boolean;
  onViewDetails?: () => void;
  /** When false, the section title is hidden (use as a sub-section without a header). Defaults to true. */
  showTitle?: boolean;
  className?: string;
}

const ContainedApproachesSummaryMolecule: React.FC<
  ContainedApproachesSummaryMoleculeProps
> = ({
  subApproaches,
  resolvedApproaches,
  loading = false,
  onViewDetails,
  showTitle = true,
  className = '',
}) => {
  const { t } = useTranslation();

  const classes = ['marketplace-contained-approaches-summary-molecule', className]
    .filter(Boolean)
    .join(' ');

  if (loading) {
    return (
      <div className={classes}>
        {showTitle && (
          <h3 className="marketplace-contained-approaches-summary-molecule__title">
            {t('marketplace.approachDetail.containedApproaches.title')}
          </h3>
        )}
        <div className="marketplace-contained-approaches-summary-molecule__list">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="marketplace-contained-approaches-summary-molecule__card marketplace-contained-approaches-summary-molecule__card--skeleton"
            >
              <div className="marketplace-contained-approaches-summary-molecule__skeleton-name" />
              <div className="marketplace-contained-approaches-summary-molecule__skeleton-desc" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!subApproaches || subApproaches.length === 0) {
    return null;
  }

  const getDisplayName = (systemName: string): string => {
    const resolved = resolvedApproaches?.find(
      (ra) => ra.approachSystemName === systemName
    );
    return resolved?.displayName ?? formatSystemName(systemName);
  };

  const getDescription = (systemName: string): string | null => {
    const resolved = resolvedApproaches?.find(
      (ra) => ra.approachSystemName === systemName
    );
    return resolved?.description ?? null;
  };

  return (
    <div className={classes}>
      {showTitle && (
        <h3 className="marketplace-contained-approaches-summary-molecule__title">
          {t('marketplace.approachDetail.containedApproaches.title')}
        </h3>
      )}
      <div className="marketplace-contained-approaches-summary-molecule__list">
        {subApproaches.map((ref) => {
          const description = getDescription(ref.approachSystemName);
          return (
            <div
              key={ref.approachSystemName}
              className="marketplace-contained-approaches-summary-molecule__card"
            >
              <div className="marketplace-contained-approaches-summary-molecule__card-header">
                <i className="pi pi-box" />
                <span className="marketplace-contained-approaches-summary-molecule__card-name">
                  {getDisplayName(ref.approachSystemName)}
                </span>
              </div>
              {description && (
                <div className="marketplace-contained-approaches-summary-molecule__card-description">
                  {description}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {onViewDetails && (
        <button
          type="button"
          className="marketplace-contained-approaches-summary-molecule__view-details"
          onClick={onViewDetails}
        >
          <i className="pi pi-sitemap" />
          {t('marketplace.approachDetail.containedApproaches.viewFullStructure')}
        </button>
      )}
    </div>
  );
};

export default ContainedApproachesSummaryMolecule;
